import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";

interface OtpInputProps {
    length?: number;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    disabled?: boolean;
    autoFocus?: boolean;
    label?: string
}

const OtpInput: React.FC<OtpInputProps> = ({
    length = 6,
    value,
    onChange,
    error,
    disabled = false,
    autoFocus = true,
    label
}) => {
    const [otp, setOtp] = useState<string[]>(
        Array(length).fill("")
    );

    const inputs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        const values = value.split("").slice(0, length);

        setOtp([
            ...values,
            ...Array(length - values.length).fill(""),
        ]);
    }, [value, length]);

    useEffect(() => {
        if (autoFocus) {
            inputs.current[0]?.focus();
        }
    }, [autoFocus]);

    const updateOtp = (newOtp: string[]) => {
        setOtp(newOtp);
        onChange(newOtp.join(""));
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        const val = e.target.value.replace(/\D/g, "");

        if (!val) {
            const newOtp = [...otp];
            newOtp[index] = "";
            updateOtp(newOtp);
            return;
        }

        const newOtp = [...otp];
        newOtp[index] = val.charAt(val.length - 1);

        updateOtp(newOtp);

        if (index < length - 1) {
            inputs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
        index: number
    ) => {
        if (e.key === "Backspace") {
            if (otp[index]) {
                const newOtp = [...otp];
                newOtp[index] = "";
                updateOtp(newOtp);
            } else if (index > 0) {
                inputs.current[index - 1]?.focus();
            }
        }

        if (e.key === "ArrowLeft" && index > 0) {
            inputs.current[index - 1]?.focus();
        }

        if (e.key === "ArrowRight" && index < length - 1) {
            inputs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (
        e: React.ClipboardEvent<HTMLInputElement>
    ) => {
        e.preventDefault();

        const pasted = e.clipboardData
            .getData("text")
            .replace(/\D/g, "")
            .slice(0, length);

        if (!pasted) return;

        const newOtp = pasted.split("");

        while (newOtp.length < length) {
            newOtp.push("");
        }

        updateOtp(newOtp);

        const focusIndex = Math.min(
            pasted.length,
            length - 1
        );

        inputs.current[focusIndex]?.focus();
    };

    return (
        <div className="w-full mt-4">
            {label && <label className="mb-1 block text-sm text-white ">{label}
                <span className="ml-1 text-red-500">*</span>
            </label>}
            <div className="flex w-full justify-center gap-1.5 text-white sm:gap-3">
                {otp.map((digit, index) => (
                    <input
                        key={index}
                        ref={(el) => {
                            inputs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={1}
                        disabled={disabled}
                        value={digit}
                        onChange={(e) => handleChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        className={clsx(
                            // Fluid width so 6 boxes always fit narrow screens; capped on larger ones.
                            "h-12 w-full min-w-0 max-w-12 flex-1 rounded-xl border bg-white/5 text-center text-lg font-semibold outline-none transition-all duration-200",
                            "focus:border-white/60 focus:bg-white/10 focus:ring-2 focus:ring-blue-400",
                            "sm:h-14 sm:max-w-14 sm:text-xl",
                            error
                                ? "border-red-500"
                                : "border-white/20",
                            disabled && "cursor-not-allowed opacity-50"
                        )}
                    />
                ))}
            </div>

            {error && (
                <p className="mt-2 text-center text-sm text-red-500">
                    {error}
                </p>
            )}
        </div>
    );
};

export default OtpInput;